// CB:BEGIN getApiUrl-config
// Helper to get API base URL for use in fetch calls.
// In production (corebids.com), uses relative URLs (same origin).
// In local dev, returns '' (empty) so calls are relative (/api/...) and
// Vite's proxy forwards them to http://127.0.0.1:3001 — this avoids the
// macOS localhost→::1 (IPv6) issue that causes ERR_CONNECTION_REFUSED when
// the backend only has an IPv4 socket.
export function getApiBaseUrl(): string {
  // CB:BEGIN production-relative-urls
  const envApiBase = ((import.meta as any)?.env?.VITE_API_BASE_URL || '').replace(/\/+$/, '');
  const DEV_API_FALLBACK = 'https://api.dev.corebids.com';
  const envBaseOr = (fallback: string) => envApiBase || fallback;

  if (typeof window !== 'undefined') {
    const host = window.location.hostname.toLowerCase();

    // CB:BEGIN local-dev-proxy
    // On localhost/127.0.0.1, use Vite proxy (relative URLs) unless VITE_API_BASE_URL
    // is explicitly set to a remote host.  This prevents the IPv6 ERR_CONNECTION_REFUSED
    // bug: browser resolves "localhost" → ::1, but Node only has an IPv4 socket.
    if (host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local')) {
      if (envApiBase && !envApiBase.includes('localhost') && !envApiBase.includes('127.0.0.1')) {
        // Explicit remote override (e.g. staging) — honour it
        return envApiBase;
      }
      // Use relative URLs → Vite proxy → http://127.0.0.1:3001
      return '';
    }
    // CB:END local-dev-proxy

    // CB:BEGIN dev-corebids-api-base
    if (host === 'dev.corebids.com' || host === 'demo.corebids.com') {
      return envBaseOr(DEV_API_FALLBACK);
    }
    if (host === 'corebids.com' || host === 'www.corebids.com' || host.endsWith('.corebids.com')) {
      return envBaseOr('');
    }
    // CB:END dev-corebids-api-base

    return envApiBase || '';
  }

  // SSR / build time — use env var or empty (relative)
  return envApiBase || '';
  // CB:END production-relative-urls
}
// CB:END getApiUrl-config

export function apiUrl(path: string): string {
  const base = getApiBaseUrl();
  // Ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  
  if (base) {
    // If base URL is set, use absolute URL
    return `${base}${normalizedPath}`;
  }
  // Fallback to relative path (for Vite proxy when env var not set)
  return normalizedPath;
}

// Back-compat export used elsewhere
export function getApiUrl(path: string): string {
  return apiUrl(path);
}

// Export getApiBase for backward compatibility (but it's the same as getApiBaseUrl)
export function getApiBase(): string {
  return getApiBaseUrl();
}

// CB:BEGIN fix-tdz-default-export
// Removed default export to avoid TDZ issues - use named exports instead
// All imports should use: import { getApiUrl } from './getApiUrl'
// CB:END fix-tdz-default-export
